import { nonAdmissionTemplates } from "../formTemplates.js";
import { userOverridableConfigurationKeys, } from "../types/configuration.js";
export const defaultConfiguration = {
    enabledForms: nonAdmissionTemplates.map((t) => ({
        id: t.id,
        version: t.version,
    })),
    mhaAdmissionsEnabled: true,
    mhaAdmissionsJointMedRecsEnabled: true,
    acdEnabled: false,
    mcaEnabled: false,
    mcaOversightEnabled: false,
    s62Enabled: false,
    uploadFormsEnabled: false,
    patientStateEnabled: false,
    patientListEnabled: false,
    s132RecordingEnabled: false,
    rioInstancesEnabled: [],
    tppInstancesEnabled: [],
    genericInstancesEnabled: [],
    helpPageUrl: "https://thalamos.atlassian.net/wiki/spaces/TEHG/overview",
    supportPageUrl: "https://survey.zohopublic.eu/zs/QfCEbJ",
    nhsNumberEnabled: false,
    rioUploadFilenameTemplate: "{{patient.givenName}}{{patient.familyName}}_{{form.typeCode}}_{{form.name}}_{{dayMonthYear form.dateUpdated}}.pdf",
    rioUploadTitleTemplate: "{{form.typeCode}}_{{form.name}}_{{dayMonthYear form.dateUpdated}}",
    rioUploadDescriptionTemplate: "Document generated by Thalamos\n{{initialFilename}}",
    rioBulkUploadFilenameTemplate: "{{spaceToUnderscore formContext.description}}.pdf",
    rioBulkUploadTitleTemplate: "{{formContext.description}} Paperwork",
    rioBulkUploadDescriptionTemplate: "Document generated by Thalamos\n{{initialFilename}}\n{{formContext.formNames}}",
    // By default no terms and conditions are needed for users
    // as they are accepted by the organisation itself
    // as part of their contract with Thalamos.
    // This field is more for users who work independently or as guests
    // where we don't know if we have a contractual relationship with the organisation.
    termsAndConditions: null,
    // Most organisation are fine with the full address
    // so this will probably only be turned on for Dorset
    excludeAdministrativeAreaFromAddress: false,
    pdsEnabled: false,
    pdsEndUserOrganisationODSCode: null,
    pdsAllowedPatientNhsNumber: null,
    phoneticPatientIndexSearchEnabled: false,
    patientDemographicsAlertEmail: null,
    mhaStatusReportEnabled: false,
    patientAutoMergeEnabled: false,
    pdsBatchTracingEnabled: false,
    // Enable the Section 12 Doctor Finder
    section12DoctorFinderEnabled: false,
    // Enable Section 12 Payments (depends on section12DoctorFinderEnabled)
    section12PaymentsEnabled: false,
    // Enable precise Section 12 Payments (depends on section12PaymentsEnabled)
    section12PrecisePaymentsEnabled: false,
    // Show warning when sharing forms outside the user's data controller
    dataControllerWarningEnabled: false,
    // De minimis corrections
    deMinimisCorrectionsEnabled: false,
    // Redo admission forms (non-s15)
    redoFormsEnabled: false,
    mhlAutoRouteConfig: null,
    embedDashboards: [],
    gpPracticeDisplayEnabled: false,
    officialGenderDisplayEnabled: false,
    rioEthnicityDisplayEnabled: false,
    reconciliationViaNhsNumberEnabled: false,
    section136MhaStatusCalculationEnabled: false,
    rafEnabled: false,
    tortusAvtDictationEnabled: false,
    tortusAvtFakeDictationEnabled: false,
};
/**
 * Resolves an organisation's configuration from its managing organisations
 * down, each organisation's own configuration overlaying what it inherits. The
 * user's overrides are then laid over the result, again from the root down, so
 * an override beats every organisation's setting, and of the user's overrides
 * the one on the most specific organisation wins. An override is a decision
 * about one person, which no organisation-wide setting should undo.
 */
export function mergeConfigurations(organisationId, configurations) {
    const chain = chainFromRoot(organisationId, configurations);
    const organisationsOwn = chain.reduce((merged, level) => ({ ...merged, ...level.configuration }), defaultConfiguration);
    return chain.reduce((merged, level) => ({ ...merged, ...overridesIn(level.userConfiguration) }), organisationsOwn);
}
/** The organisation and the managing organisations above it, root first. */
function chainFromRoot(organisationId, configurations) {
    const level = configurations.find((c) => c.organisationId === organisationId);
    if (!level) {
        return [];
    }
    return [
        ...chainFromRoot(level.managingOrganisationId, configurations),
        level,
    ];
}
/**
 * Only the overridable keys, and only those with a value: stored overrides may
 * hold keys that are no longer overridable, and an undefined key must inherit
 * rather than overwrite.
 */
function overridesIn(userConfiguration) {
    const overrides = {};
    for (const key of userOverridableConfigurationKeys) {
        const value = userConfiguration[key];
        if (value !== undefined) {
            overrides[key] = value;
        }
    }
    return overrides;
}
// ---------------------------------------------------------------------------
// Dr Finder — role-targeted accessor helpers.
//
// Each reads the new role-targeted flag and falls back to the legacy flag when
// the new one is `undefined`. This keeps behaviour identical for existing orgs
// (which only have the legacy flags set) until an admin opts into the granular
// model. The new flags are deliberately absent from `defaultConfiguration` so
// they stay `undefined` and the `??` fallback fires — see types/configuration.ts.
//
// Because the fallback is nullish (`??`), a new flag is tri-state: leaving it
// `undefined` inherits the legacy flag, while an explicit `false` turns the
// capability OFF even when the legacy flag is on. This gives admins full on/off
// control WITHOUT mutating the legacy flags. Read capabilities through these
// helpers rather than the raw flags so the fallback lives in exactly one place.
// ---------------------------------------------------------------------------
/** AMHP: can AMHPs in this org search for S12 doctors ("DrFinder Search")? */
export function canAmhpSearchS12Doctors(configuration) {
    return (configuration.section12AmhpSearchEnabled ??
        configuration.section12DoctorFinderEnabled);
}
/** AMHP team: can this team receive and approve S12 payment requests? */
export function canAmhpTeamApproveS12Payments(configuration) {
    return (configuration.section12AmhpApprovePaymentsEnabled ??
        configuration.section12PaymentsEnabled);
}
/** AMHP team: does this team see precise (vs estimated) S12 payment fees? */
export function canAmhpTeamUsePreciseS12Payments(configuration) {
    return (configuration.section12AmhpPrecisePaymentsEnabled ??
        configuration.section12PrecisePaymentsEnabled);
}
/** Doctor: is this doctor's S12 profile searchable by AMHPs? */
export function isDoctorS12Searchable(configuration) {
    return (configuration.section12DoctorSearchableEnabled ??
        configuration.section12DoctorFinderEnabled);
}
/**
 * Doctor: can a doctor in this org initiate (submit) S12 payment requests?
 * UI-visibility only — the backend submission authority remains the doctor's
 * GMC number plus the target AMHP team approving payments.
 */
export function canDoctorInitiateS12Payments(configuration) {
    return (configuration.section12DoctorInitiatePaymentsEnabled ??
        configuration.section12PaymentsEnabled);
}
/**
 * Doctor: can a (full) doctor in this org invite a guest (unregistered) AMHP as
 * the recipient of an S12 payment request?
 *
 * Unlike the other Dr-Finder helpers this is a BRAND-NEW capability with NO
 * legacy flag to fall back to, so it simply reads the field: undefined/false
 * means OFF, only an explicit `true` enables it.
 */
export function canDoctorInviteGuestS12Amhp(configuration) {
    return configuration.section12DoctorInviteGuestAmhpEnabled === true;
}
/**
 * @deprecated Use the role-specific helpers instead. Retained as an alias for
 * {@link canAmhpTeamApproveS12Payments} (its only existing call sites all read
 * an AMHP team's org config). New code should call the named helper directly.
 */
export function isSection12PaymentsEnabled(configuration) {
    return canAmhpTeamApproveS12Payments(configuration);
}
/**
 * Whether the Aspire-level reconciliation feature (NHS number click) is
 * enabled for an organisation. Single source of truth for the flag
 * dependencies, shared by the frontend eligibility helper
 * (`canReconcileViaAspireFlag`) and the server authorisation checks.
 *
 * Requires all of:
 * - `reconciliationViaNhsNumberEnabled` (the feature itself)
 * - `nhsNumberEnabled` (dependency)
 * - `pdsEnabled` (dependency)
 * - `pdsEndUserOrganisationODSCode` configured (a PDS ODS code to search with)
 *
 * This is purely an org-config check. The "patient must not already have an
 * NHS number" condition is a UI gate (see `canReconcileViaAspireFlag`), not
 * an authorisation rule, so it is intentionally not included here.
 */
export function hasAspireReconciliationFlag(configuration) {
    return (configuration.reconciliationViaNhsNumberEnabled &&
        configuration.nhsNumberEnabled &&
        configuration.pdsEnabled &&
        !!configuration.pdsEndUserOrganisationODSCode);
}
/**
 * Returns whether the given form context type (and its forms) is enabled for
 * the organisation described by {@link orgConfig}.
 *
 * - `"admission"` → gated by `mhaAdmissionsEnabled`
 * - `"standalone"` → at least one form template must appear in `enabledForms`
 * - `"police-incident"` → always enabled (no org config gating)
 * - `"soad-assessment"` → gated by the `"soad-report-form"` template (id + version) being present in `enabledForms`
 * - `"mca-assessment"` → at least one form template must appear in `enabledForms`
 */
export function isFormContextTypeEnabledForOrg(formContextType, forms, orgConfig) {
    switch (formContextType) {
        case "admission":
            return orgConfig.mhaAdmissionsEnabled;
        case "standalone":
            return forms.some((f) => orgConfig.enabledForms.some((ef) => ef.id === f.formTemplate.id &&
                ef.version === f.formTemplate.version));
        case "soad-assessment":
            return forms
                .filter((f) => f.formTemplate.id === "soad-report-form")
                .some((f) => orgConfig.enabledForms.some((ef) => ef.id === f.formTemplate.id &&
                ef.version === f.formTemplate.version));
        case "mca-assessment":
            return forms.some((f) => orgConfig.enabledForms.some((ef) => ef.id === f.formTemplate.id &&
                ef.version === f.formTemplate.version));
        case "police-incident":
            return true;
        default: {
            const _exhaustive = formContextType;
            throw new Error(`Unhandled form context type: ${String(_exhaustive)}`);
        }
    }
}
export const domainRegex = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.){1,}([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$/;
//# sourceMappingURL=configuration.js.map